import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, LucideIcon, Star } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import PortfolioDialog from "@/components/PortfolioDialog";
import enterpriseTechBg from "@/assets/enterprise-tech-bg.jpg";
import mobileAppBg from "@/assets/mobile-app-bg.jpg";
import cloudComputingBg from "@/assets/cloud-computing-bg.jpg";
import cybersecurityBg from "@/assets/cybersecurity-bg.jpg";
import aiMachineLearningBg from "@/assets/ai-machine-learning-bg.jpg";
import devopsAutomationBg from "@/assets/devops-automation-bg.jpg";
import analyticsDashboardBg from "@/assets/analytics-dashboard-bg.jpg";
import {
  Code,
  Smartphone,
  Globe,
  Database,
  Cloud,
  Shield,
  Settings,
  Users,
  Headphones,
  Cpu,
  BarChart3,
  Palette,
  Search,
  MessageSquare,
  Workflow,
  Zap,
  Target,
  FileText,
  Briefcase,
  Monitor,
  Layers,
  GitBranch,
  TestTube,
  Rocket,
  Wrench,
  Eye,
  Lock,
  Server,
  Bot,
  Brain,
  Network,
  HardDrive,
  Wifi,
  Smartphone as Mobile,
  Tablet,
  Watch,
} from "lucide-react";

interface ServiceData {
  icon: LucideIcon;
  title: string;
  category: string;
  description: string;
  features: string[];
  technologies: string[];
  benefits: string[];
  process: string[];
  overview: string;
  pricing: {
    starter: { price: string; features: string[] };
    professional: { price: string; features: string[] };
    enterprise: { price: string; features: string[] };
  };
}

const serviceData: Record<string, ServiceData> = {
  "custom-software": {
    icon: Code,
    title: "Custom Software Development",
    category: "Development",
    description:
      "Build tailored software solutions that perfectly match your business requirements and workflows.",
    overview:
      "Our custom software development services deliver bespoke solutions that perfectly align with your business processes and goals. We work closely with you to understand your unique requirements and create software that drives efficiency and growth.",
    features: [
      "Custom Web Applications",
      "Enterprise Software",
      "Legacy System Modernization",
      "API Development",
      "Full-stack Development",
      "Microservices Architecture",
    ],
    technologies: [
      "React",
      "Node.js",
      "Python",
      "Java",
      ".NET",
      "PostgreSQL",
      "MongoDB",
      "AWS",
      "Docker",
      "Kubernetes",
    ],
    benefits: [
      "Scalable Architecture",
      "Technology Stack Flexibility",
      "Custom Integrations",
      "Ongoing Support",
      "Faster Time-to-Market",
      "Cost-effective Solutions",
    ],
    process: [
      "Requirements Analysis",
      "System Architecture Design",
      "Development & Testing",
      "Deployment & Launch",
      "Maintenance & Support",
    ],
    pricing: {
      starter: {
        price: "Starting at $5,000",
        features: [
          "Basic web application",
          "1-3 months timeline",
          "Basic support",
          "Source code included",
        ],
      },
      professional: {
        price: "Starting at $15,000",
        features: [
          "Complex enterprise solution",
          "3-6 months timeline",
          "Priority support",
          "Advanced integrations",
          "Performance optimization",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Large-scale systems",
          "6+ months timeline",
          "24/7 dedicated support",
          "Custom architecture",
          "Multi-platform deployment",
        ],
      },
    },
  },
  "mobile-development": {
    icon: Smartphone,
    title: "Mobile App Development",
    category: "Mobile",
    description:
      "Create engaging mobile applications for iOS and Android platforms with native performance.",
    overview:
      "We specialize in creating high-performance mobile applications that provide seamless user experiences across all platforms. Our expertise spans native iOS and Android development as well as cross-platform solutions.",
    features: [
      "Native iOS Apps",
      "Android Applications",
      "Cross-platform Development",
      "App Store Optimization",
      "UI/UX Design",
      "Backend Integration",
    ],
    technologies: [
      "React Native",
      "Flutter",
      "Swift",
      "Kotlin",
      "Xamarin",
      "Firebase",
      "AWS Mobile",
      "REST APIs",
    ],
    benefits: [
      "Cross-platform Compatibility",
      "Native Performance",
      "App Store Optimization",
      "Push Notifications",
      "Offline Functionality",
      "Real-time Updates",
    ],
    process: [
      "Market Research",
      "UI/UX Design",
      "Development",
      "Testing",
      "App Store Submission",
      "Post-launch Support",
    ],
    pricing: {
      starter: {
        price: "Starting at $8,000",
        features: [
          "Simple mobile app",
          "2-4 months timeline",
          "Basic features",
          "App store submission",
        ],
      },
      professional: {
        price: "Starting at $20,000",
        features: [
          "Advanced mobile app",
          "4-6 months timeline",
          "Custom features",
          "Backend integration",
          "Analytics",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise mobile solution",
          "6+ months timeline",
          "Advanced security",
          "Custom integrations",
          "Ongoing maintenance",
        ],
      },
    },
  },
  "web-development": {
    icon: Globe,
    title: "Web Development",
    category: "Web",
    description:
      "Develop responsive, fast, and user-friendly websites and web applications.",
    overview:
      "Our web development services create modern, responsive, and high-performing websites and web applications that engage users and drive business growth.",
    features: [
      "Responsive Design",
      "E-commerce Solutions",
      "Progressive Web Apps",
      "Content Management Systems",
      "SEO Optimization",
      "Performance Optimization",
    ],
    technologies: [
      "React",
      "Vue.js",
      "Angular",
      "Next.js",
      "WordPress",
      "Shopify",
      "Node.js",
      "PHP",
    ],
    benefits: [
      "Mobile-first Design",
      "Fast Loading Times",
      "SEO Optimized",
      "Scalable Architecture",
      "Cross-browser Compatibility",
      "Security Best Practices",
    ],
    process: [
      "Planning & Strategy",
      "Design & Prototyping",
      "Development",
      "Testing & QA",
      "Launch",
      "Maintenance",
    ],
    pricing: {
      starter: {
        price: "Starting at $3,000",
        features: [
          "Basic website",
          "5-10 pages",
          "Responsive design",
          "Basic SEO",
        ],
      },
      professional: {
        price: "Starting at $8,000",
        features: [
          "Advanced website",
          "Custom features",
          "CMS integration",
          "E-commerce functionality",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise web application",
          "Custom development",
          "Advanced integrations",
          "High-performance optimization",
        ],
      },
    },
  },
  "cloud-migration": {
    icon: Cloud,
    title: "Cloud Migration",
    category: "Cloud",
    description:
      "Seamlessly migrate your infrastructure to cloud platforms for improved scalability and cost efficiency.",
    overview:
      "Our cloud migration services help you transition to modern cloud infrastructure with minimal downtime and maximum efficiency. We ensure your applications are optimized for cloud environments.",
    features: [
      "Infrastructure Assessment",
      "Migration Strategy",
      "Data Migration",
      "Cloud Optimization",
      "Security Implementation",
      "Cost Optimization",
    ],
    technologies: [
      "AWS",
      "Azure",
      "Google Cloud",
      "Docker",
      "Kubernetes",
      "Terraform",
      "Ansible",
      "CloudFormation",
      "AWS Snowflake",
      "Azure Architect",
    ],
    benefits: [
      "Improved Scalability",
      "Cost Reduction",
      "Enhanced Security",
      "Better Performance",
      "Disaster Recovery",
      "Global Accessibility",
    ],
    process: [
      "Assessment & Planning",
      "Architecture Design",
      "Migration Execution",
      "Testing & Validation",
      "Optimization",
      "Ongoing Management",
    ],
    pricing: {
      starter: {
        price: "Starting at $10,000",
        features: [
          "Small infrastructure migration",
          "Basic cloud setup",
          "2-4 weeks timeline",
          "Documentation",
        ],
      },
      professional: {
        price: "Starting at $25,000",
        features: [
          "Medium enterprise migration",
          "Advanced optimization",
          "4-8 weeks timeline",
          "Training included",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Large-scale migration",
          "Custom architecture",
          "8+ weeks timeline",
          "24/7 support",
          "Compliance included",
        ],
      },
    },
  },
  cybersecurity: {
    icon: Shield,
    title: "Cybersecurity Services",
    category: "Security",
    description:
      "Protect your digital assets with comprehensive security solutions and assessments.",
    overview:
      "Our cybersecurity services provide comprehensive protection for your digital assets, ensuring compliance with industry standards and protection against evolving threats.",
    features: [
      "Security Audits",
      "Penetration Testing",
      "Compliance Consulting",
      "Incident Response",
      "Security Training",
      "Vulnerability Assessment",
    ],
    technologies: [
      "OWASP",
      "ISO 27001",
      "SOC 2",
      "GDPR",
      "PCI DSS",
      "NIST Framework",
      "Security Tools",
      "Monitoring Systems",
    ],
    benefits: [
      "Enhanced Security Posture",
      "Compliance Assurance",
      "Risk Mitigation",
      "Incident Prevention",
      "Employee Training",
      "Continuous Monitoring",
    ],
    process: [
      "Security Assessment",
      "Risk Analysis",
      "Implementation Plan",
      "Security Controls Deployment",
      "Testing & Validation",
      "Ongoing Monitoring",
    ],
    pricing: {
      starter: {
        price: "Starting at $5,000",
        features: [
          "Basic security audit",
          "Vulnerability scan",
          "Security recommendations",
          "2-week timeline",
        ],
      },
      professional: {
        price: "Starting at $15,000",
        features: [
          "Comprehensive security assessment",
          "Penetration testing",
          "Compliance review",
          "4-week timeline",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise security program",
          "Ongoing monitoring",
          "Incident response",
          "Compliance management",
        ],
      },
    },
  },
  "data-analytics": {
    icon: BarChart3,
    title: "Data Analytics & BI",
    category: "Analytics",
    description:
      "Transform your data into actionable insights with advanced analytics and business intelligence.",
    overview:
      "Our data analytics and business intelligence services help you unlock the power of your data to make informed decisions and drive business growth.",
    features: [
      "Data Visualization",
      "Predictive Analytics",
      "Real-time Dashboards",
      "Report Automation",
      "Data Mining",
      "Machine Learning Models",
    ],
    technologies: [
      "Tableau",
      "Power BI",
      "Python",
      "R",
      "Apache Spark",
      "Snowflake",
      "SQL",
      "MongoDB",
    ],
    benefits: [
      "Data-driven Decisions",
      "Improved Efficiency",
      "Predictive Insights",
      "Cost Optimization",
      "Performance Monitoring",
      "Competitive Advantage",
    ],
    process: [
      "Data Assessment",
      "Strategy Development",
      "Dashboard Design",
      "Implementation",
      "Training",
      "Ongoing Support",
    ],
    pricing: {
      starter: {
        price: "Starting at $7,000",
        features: [
          "Basic dashboards",
          "Standard reports",
          "4-6 weeks timeline",
          "Training included",
        ],
      },
      professional: {
        price: "Starting at $20,000",
        features: [
          "Advanced analytics",
          "Custom dashboards",
          "6-10 weeks timeline",
          "Predictive models",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise BI platform",
          "Real-time analytics",
          "10+ weeks timeline",
          "Advanced ML models",
        ],
      },
    },
  },
  "database-management": {
    icon: Database,
    title: "Database Management",
    category: "Data",
    description:
      "Optimize and manage your databases for maximum performance and reliability.",
    overview:
      "Our database management services ensure your data infrastructure is optimized, secure, and performs at peak efficiency to support your business operations.",
    features: [
      "Database Design",
      "Performance Tuning",
      "Data Migration",
      "Backup Solutions",
      "Security Implementation",
      "Monitoring & Maintenance",
    ],
    technologies: [
      "PostgreSQL",
      "MongoDB",
      "MySQL",
      "Redis",
      "Elasticsearch",
      "Cassandra",
      "Oracle",
      "SQL Server",
    ],
    benefits: [
      "Improved Performance",
      "Data Security",
      "High Availability",
      "Automated Backups",
      "Cost Optimization",
      "Scalable Architecture",
    ],
    process: [
      "Database Assessment",
      "Architecture Design",
      "Implementation",
      "Performance Optimization",
      "Security Setup",
      "Ongoing Monitoring",
    ],
    pricing: {
      starter: {
        price: "Starting at $4,000",
        features: [
          "Basic database setup",
          "Performance tuning",
          "Basic monitoring",
          "2-3 weeks timeline",
        ],
      },
      professional: {
        price: "Starting at $12,000",
        features: [
          "Complex database solutions",
          "Advanced optimization",
          "Security implementation",
          "4-6 weeks timeline",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise database architecture",
          "24/7 monitoring",
          "High availability setup",
          "Ongoing maintenance",
        ],
      },
    },
  },
  "ai-ml": {
    icon: Cpu,
    title: "AI & Machine Learning",
    category: "AI/ML",
    description:
      "Implement intelligent solutions using cutting-edge AI and machine learning technologies.",
    overview:
      "Our AI and machine learning services help you leverage artificial intelligence to automate processes, gain insights, and create intelligent applications that drive business value.",
    features: [
      "Natural Language Processing",
      "Computer Vision",
      "Predictive Modeling",
      "Recommendation Systems",
      "Deep Learning",
      "MLOps",
    ],
    technologies: [
      "TensorFlow",
      "PyTorch",
      "OpenAI",
      "Scikit-learn",
      "Hugging Face",
      "AWS ML",
      "Azure ML",
      "Google AI",
    ],
    benefits: [
      "Process Automation",
      "Predictive Insights",
      "Enhanced User Experience",
      "Cost Reduction",
      "Competitive Advantage",
      "Scalable Intelligence",
    ],
    process: [
      "Problem Definition",
      "Data Analysis",
      "Model Development",
      "Training & Testing",
      "Deployment",
      "Monitoring & Optimization",
    ],
    pricing: {
      starter: {
        price: "Starting at $10,000",
        features: [
          "Basic ML model",
          "Data analysis",
          "Simple deployment",
          "6-8 weeks timeline",
        ],
      },
      professional: {
        price: "Starting at $25,000",
        features: [
          "Advanced AI solution",
          "Custom models",
          "Production deployment",
          "8-12 weeks timeline",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise AI platform",
          "Complex ML pipelines",
          "Continuous learning",
          "Ongoing optimization",
        ],
      },
    },
  },
  devops: {
    icon: Settings,
    title: "DevOps & Automation",
    category: "DevOps",
    description:
      "Streamline your development and deployment processes with modern DevOps practices.",
    overview:
      "Our DevOps services help you implement continuous integration, continuous deployment, and automation practices that accelerate development and improve reliability.",
    features: [
      "CI/CD Pipelines",
      "Infrastructure as Code",
      "Monitoring & Logging",
      "Automated Testing",
      "Container Orchestration",
      "Security Integration",
    ],
    technologies: [
      "Jenkins",
      "GitLab CI",
      "Docker",
      "Kubernetes",
      "Ansible",
      "Prometheus",
      "Grafana",
      "Terraform",
    ],
    benefits: [
      "Faster Deployments",
      "Improved Reliability",
      "Reduced Manual Errors",
      "Better Collaboration",
      "Cost Optimization",
      "Enhanced Security",
    ],
    process: [
      "Current State Assessment",
      "Pipeline Design",
      "Tool Implementation",
      "Automation Setup",
      "Testing & Validation",
      "Ongoing Optimization",
    ],
    pricing: {
      starter: {
        price: "Starting at $6,000",
        features: [
          "Basic CI/CD setup",
          "Automation scripts",
          "Basic monitoring",
          "3-4 weeks timeline",
        ],
      },
      professional: {
        price: "Starting at $18,000",
        features: [
          "Complete DevOps pipeline",
          "Advanced automation",
          "Comprehensive monitoring",
          "6-8 weeks timeline",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise DevOps platform",
          "Multi-environment setup",
          "Advanced security",
          "Ongoing support",
        ],
      },
    },
  },
  "ui-ux-design": {
    icon: Palette,
    title: "UI/UX Design",
    category: "Design",
    description:
      "Create intuitive and beautiful user experiences that delight your customers.",
    overview:
      "Our UI/UX design services focus on creating user-centered designs that not only look beautiful but also provide exceptional user experiences that drive engagement and conversion.",
    features: [
      "User Research",
      "Wireframing",
      "Prototyping",
      "Design Systems",
      "Usability Testing",
      "Responsive Design",
    ],
    technologies: [
      "Figma",
      "Adobe XD",
      "Sketch",
      "InVision",
      "Principle",
      "Framer",
      "Marvel",
      "Zeplin",
    ],
    benefits: [
      "Improved User Satisfaction",
      "Higher Conversion Rates",
      "Reduced Development Time",
      "Brand Consistency",
      "Better Usability",
      "Competitive Advantage",
    ],
    process: [
      "User Research",
      "Information Architecture",
      "Wireframing",
      "Visual Design",
      "Prototyping",
      "Testing & Iteration",
    ],
    pricing: {
      starter: {
        price: "Starting at $3,500",
        features: [
          "Basic UI design",
          "Mobile-responsive",
          "Style guide",
          "4-6 weeks timeline",
        ],
      },
      professional: {
        price: "Starting at $8,500",
        features: [
          "Complete UX/UI design",
          "User research",
          "Interactive prototypes",
          "6-10 weeks timeline",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Enterprise design system",
          "Multi-platform design",
          "Ongoing design support",
          "Brand guidelines",
        ],
      },
    },
  },
  support: {
    icon: Headphones,
    title: "24/7 Support & Maintenance",
    category: "Support",
    description:
      "Ensure your systems run smoothly with round-the-clock support and maintenance.",
    overview:
      "Our support and maintenance services provide continuous monitoring, proactive maintenance, and rapid issue resolution to keep your systems running at peak performance.",
    features: [
      "System Monitoring",
      "Bug Fixes",
      "Performance Optimization",
      "Security Updates",
      "Backup Management",
      "Technical Support",
    ],
    technologies: [
      "Monitoring Tools",
      "Ticketing Systems",
      "Remote Support",
      "Maintenance Protocols",
      "Security Scanners",
      "Performance Analyzers",
    ],
    benefits: [
      "Maximum Uptime",
      "Proactive Issue Prevention",
      "Rapid Problem Resolution",
      "Cost Predictability",
      "Security Assurance",
      "Peace of Mind",
    ],
    process: [
      "System Assessment",
      "Monitoring Setup",
      "Support Protocol",
      "Maintenance Schedule",
      "Issue Resolution",
      "Continuous Improvement",
    ],
    pricing: {
      starter: {
        price: "Starting at $1,500/month",
        features: [
          "Business hours support",
          "Basic monitoring",
          "Monthly maintenance",
          "Email support",
        ],
      },
      professional: {
        price: "Starting at $3,500/month",
        features: [
          "24/7 monitoring",
          "Priority support",
          "Weekly maintenance",
          "Phone & email support",
        ],
      },
      enterprise: {
        price: "Custom Quote",
        features: [
          "Dedicated support team",
          "SLA guarantees",
          "Custom maintenance",
          "On-site support available",
        ],
      },
    },
  },
};

const ServiceDetail = () => {
  const { serviceId } = useParams<{ serviceId: string }>();

  // Force re-render when serviceId changes
  useEffect(() => {
    // Scroll to top immediately and after a short delay to ensure it works
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [serviceId]);

  if (!serviceId || !serviceData[serviceId]) {
    return <Navigate to="/404" replace />;
  }

  const service = serviceData[serviceId];
  const IconComponent = service.icon;

  // Select background image based on service type
  const getBackgroundImage = (serviceId: string) => {
    const backgroundMap: Record<string, string> = {
      "mobile-development": mobileAppBg,
      "cloud-migration": cloudComputingBg,
      cybersecurity: cybersecurityBg,
      "ai-ml": aiMachineLearningBg,
      devops: devopsAutomationBg,
      "data-analytics": analyticsDashboardBg,
      "database-management": analyticsDashboardBg,
    };
    return backgroundMap[serviceId] || enterpriseTechBg;
  };

  const backgroundImage = getBackgroundImage(serviceId);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={backgroundImage}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          {/* <Button
            variant="outline"
            className="mb-8 border-white text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Button> */}

          <div className="max-w-4xl text-white">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-gradient-to-br from-primary to-secondary p-4 rounded-xl shadow-glow">
                <IconComponent className="h-8 w-8 text-white" />
              </div>
              <Badge
                variant="secondary"
                className="bg-white/10 text-white border-white/20"
              >
                {service.category}
              </Badge>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white  mt-12">
              {service.title}
            </h1>

            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-foreground">
                Overview
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {service.overview}
              </p>

              <h3 className="text-xl font-semibold mb-4 text-foreground">
                Key Benefits
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className={`h-5 w-5 flex-shrink-0 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'}`} />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <Card className="bg-gradient-card border-0 shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-primary" />
                  Key Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {service.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-background/50 rounded-lg"
                    >
                      <div className={`w-2 h-2 rounded-full ${index % 2 === 0 ? 'bg-primary shadow-glow' : 'bg-secondary shadow-glow-secondary'}`}></div>
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-accent/5">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Technologies We Use
          </h2>
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            {service.technologies.map((tech, index) => (
              <Badge
                key={index}
                variant="outline"
                className="px-4 py-2 text-sm"
              >
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">
            Our Process
          </h2>
          <div className="grid md:grid-cols-5 gap-6 max-w-5xl mx-auto">
            {service.process.map((step, index) => (
              <div key={index} className="text-center">
                <div className={`${index % 2 === 0 ? 'bg-gradient-primary' : 'bg-gradient-to-br from-secondary to-secondary-hover'} text-white w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4 shadow-glow`}>
                  {index + 1}
                </div>
                <h3 className="font-semibold mb-2 text-foreground">{step}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
